import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  BookOpen, 
  Download, 
  FileText, 
  Search,
  Star,
  Clipboard,
  DollarSign,
  Users,
  ChefHat,
  Sparkles,
  ExternalLink,
  FolderOpen
} from 'lucide-react';

// A&W Resource Library
const RESOURCES = [
  {
    id: 1,
    category: "Operations",
    title: "2025 A&W Operations Manual",
    description: "Complete operational guide covering food safety, guest service, equipment, and brand standards.",
    icon: BookOpen,
    color: "bg-orange-500",
    url: "https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/yes9zsjw_2025%2BA%26W%2BOperations%2BManual%2BEnglish.pdf",
    featured: true
  },
  {
    id: 2,
    category: "5 Star Program",
    title: "5 STAR Launch Operator Guide",
    description: "Step-by-step guide for implementing the 5 Star Operating System in your restaurant.",
    icon: Star,
    color: "bg-amber-500",
    url: "https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/6gtqkau9_5%20STAR%20-%20STEP%204%20LAUNCH%20Operator%20Guide%20FINAL.pdf",
    featured: true
  },
  {
    id: 3,
    category: "Cleaning",
    title: "Be Clean - 5 Star Program",
    description: "Daily and weekly cleaning checklists and deep cleaning procedures.",
    icon: Sparkles,
    color: "bg-cyan-500",
    url: "https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/ibw7zpwv_CMX%2B-%2BBe%2BClean%2B-%2B5%2BStar%20%281%29.pdf"
  },
  {
    id: 4,
    category: "Assessments",
    title: "2025 Brand Compliance Assessment",
    description: "Assessment criteria for service, cleanliness, food quality, and brand standards.",
    icon: Clipboard,
    color: "bg-purple-500",
    url: "https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/yn4m7641_2025%2BBrand%2BCompliance%2BAssessment.pdf"
  },
  {
    id: 5,
    category: "Daily Operations",
    title: "Daily Operations Excellence Checklist",
    description: "Daily checklist for lobby, service, staff, kitchen, food safety, and leadership.",
    icon: Clipboard,
    color: "bg-green-500",
    url: "https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/kk0n7tnj_Daily_Operations_Excellence_Checklist.pdf"
  },
  {
    id: 6,
    category: "Station Training",
    title: "Station Evaluations 2024",
    description: "Evaluation criteria for all restaurant stations: Counter, Drive-Thru, Runner, Point, Fryer, Grill, Prep.",
    icon: Users,
    color: "bg-blue-500",
    url: "https://customer-assets.emergentagent.com/job_5adcc4e8-1bb2-4cdf-8c44-5c6c1ccd2118/artifacts/ysscono5_Station%2BEvaluations%2B2024.pdf"
  },
  {
    id: 7,
    category: "Profit",
    title: "Profit Improvement Program Guide",
    description: "Weekly profit improvement program focusing on sales forecasting, food cost, and labor management.",
    icon: DollarSign,
    color: "bg-emerald-500",
    url: "https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/4fpr4y1c_Profit%2BImprovement%2BProgram%2BOperator%2BGuide%2B%28Full%29.pdf"
  },
  {
    id: 8,
    category: "Profit",
    title: "Profit Improvement Playbook",
    description: "Quick reference playbook for profit improvement strategies and action items.",
    icon: DollarSign,
    color: "bg-emerald-500",
    url: "https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/6z4ggfrh_Profit%2BImprovement%2BPlaybook.pdf"
  },
  {
    id: 9,
    category: "Training",
    title: "RMTP Tracking Chart",
    description: "Restaurant Manager Training Program tracking chart for management development.",
    icon: Users,
    color: "bg-indigo-500",
    url: "https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/ngtn45u7_RMTP%2BTracking%2BChart%2B-%2BSept%2B2020.pdf"
  },
  {
    id: 10,
    category: "Cost Control",
    title: "Food Cost Troubleshooting Guide",
    description: "Troubleshooting guide for identifying and resolving food cost variance issues.",
    icon: ChefHat,
    color: "bg-red-500",
    url: "https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/954nl1oz_Food%2Bcost%2Btrouble%2Bshooting.pdf"
  }
];

const CATEGORIES = [
  { name: "All", icon: FolderOpen },
  { name: "Operations", icon: BookOpen },
  { name: "5 Star Program", icon: Star },
  { name: "Cleaning", icon: Sparkles },
  { name: "Profit", icon: DollarSign },
  { name: "Training", icon: Users },
  { name: "Station Training", icon: ChefHat },
  { name: "Assessments", icon: Clipboard },
  { name: "Daily Operations", icon: Clipboard },
  { name: "Cost Control", icon: DollarSign }
];

export default function Resources() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredResources = RESOURCES.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || resource.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredResources = RESOURCES.filter(r => r.featured);

  return (
    <div className="space-y-6" data-testid="resources-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <BookOpen className="w-8 h-8" />
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Resource Library
          </h1>
        </div>
        <p className="text-amber-100">
          Access A&W operational guides, training materials, and best practices
        </p>
      </div>

      {/* Featured Resources */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {featuredResources.map((resource) => {
          const Icon = resource.icon;
          return (
            <Card key={resource.id} className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200 rounded-sm overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 ${resource.color} rounded-lg flex items-center justify-center text-white flex-shrink-0`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                      <span className="text-xs font-semibold text-amber-600 uppercase tracking-wide">Featured</span>
                    </div>
                    <h3 className="font-bold text-slate-900 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {resource.title}
                    </h3>
                    <p className="text-sm text-slate-600 mb-4">{resource.description}</p>
                    <a href={resource.url} target="_blank" rel="noopener noreferrer">
                      <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm">
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search resources..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Category Pills - compact */}
      <div className="flex flex-wrap gap-1.5" data-testid="category-pills">
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const active = selectedCategory === cat.name;
          return (
            <button
              key={cat.name}
              onClick={() => setSelectedCategory(cat.name)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                active
                  ? 'bg-orange-500 text-white shadow-sm'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-orange-300 hover:text-orange-600'
              }`}
              data-testid={`category-${cat.name.toLowerCase()}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {cat.name}
            </button>
          );
        })}
      </div>

      {/* Resources Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="resources-grid">
        {filteredResources.map((resource) => {
          const Icon = resource.icon;
          return (
            <Card key={resource.id} className="bg-white border border-slate-200 rounded-sm hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-10 h-10 ${resource.color} rounded-lg flex items-center justify-center text-white flex-shrink-0`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      {resource.category}
                    </span>
                    <h3 className="font-bold text-slate-900 mt-0.5 truncate" title={resource.title}>
                      {resource.title}
                    </h3>
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-4 line-clamp-2">{resource.description}</p>
                <div className="flex gap-2">
                  <a href={resource.url} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button variant="outline" className="w-full rounded-sm border-orange-300 text-orange-600 hover:bg-orange-50">
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </a>
                  <a href={resource.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="ghost" size="icon" className="rounded-sm">
                      <ExternalLink className="w-4 h-4 text-slate-400" />
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredResources.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-bold text-slate-900 mb-2">No resources found</h3>
          <p className="text-slate-600">Try adjusting your search or filter</p>
        </div>
      )}
    </div>
  );
}
